import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { PlaybackState } from './types/enums';
import { getTurnScript } from './data/turn-script';
import { generateSimulationData } from './data/simulator';
import { runDiagnosticPipeline } from './algorithm';
import type { DiagnosticPipelineResult } from './algorithm';
import {
  PlaybackMachineProvider,
  usePlaybackMachine,
} from './state-machines/usePlaybackMachine';
import R1ProductHeader from './components/regions/R1ProductHeader';
import R2ReplayMain from './components/regions/R2ReplayMain';
import HeroSection from './components/narrative/HeroSection';
import PainPointSection from './components/narrative/PainPointSection';
import DeepExploreSection from './components/narrative/DeepExploreSection';
import LoadingSkeleton from './components/common/LoadingSkeleton';

// R1 (header) and R2 (replay main) are first-screen-critical and imported
// directly above — they are NOT lazy-loaded. R3-R12 are code-split via
// React.lazy(); each region's dynamic importer is also kept as a named const
// so it can be preloaded after R2 paints (see AppContent preload effect).

// Lazy-loaded regions (R3-R12).
const R3Importer = () => import('./components/regions/R3VirtualHardware');
const R4Importer = () => import('./components/regions/R4RunSummary');
const R5Importer = () => import('./components/regions/R5ScoringRadar');
const R6Importer = () => import('./components/regions/R6AlgorithmLab');
const R7Importer = () => import('./components/regions/R7Visualization3D');
const R8Importer = () => import('./components/regions/R8SensorWaveform');
const R9Importer = () => import('./components/regions/R9FallDetection');
const R10Importer = () => import('./components/regions/R10WatchShowcase');
const R11Importer = () => import('./components/regions/R11TechDepth');
const R12Importer = () => import('./components/regions/R12Footer');

const R3VirtualHardware = lazy(R3Importer);
const R4RunSummary = lazy(R4Importer);
const R5ScoringRadar = lazy(R5Importer);
const R6AlgorithmLab = lazy(R6Importer);
const R7Visualization3D = lazy(R7Importer);
const R8SensorWaveform = lazy(R8Importer);
const R9FallDetection = lazy(R9Importer);
const R10WatchShowcase = lazy(R10Importer);
const R11TechDepth = lazy(R11Importer);
const R12Footer = lazy(R12Importer);

// ===== App content (rendered inside providers) =====

/**
 * AppContent - Owns the rendered layout once the pipeline result is available.
 *
 * On mount it injects the run data into the SM-1 playback machine via
 * `loadSuccess`, transitioning it from `Loading` → `Ready`. While the machine
 * is still `Loading`, a skeleton placeholder is shown.
 */
function AppContent({
  pipelineResult,
}: {
  pipelineResult: DiagnosticPipelineResult;
}) {
  const playback = usePlaybackMachine();
  const { run_session: runSession } = pipelineResult;
  const { loadSuccess } = playback;

  // Load the run data into the playback machine exactly once. `loadSuccess`
  // is a stable callback (useCallback []), and the run data is set once, so
  // this effect fires a single time.
  useEffect(() => {
    loadSuccess(runSession.duration_ms, runSession.turns);
  }, [loadSuccess, runSession.duration_ms, runSession.turns]);

  // Preload R3-R12 chunks during the browser's idle period, after R1 + R2
  // have rendered. This warms the module cache so that when the user scrolls
  // and an IntersectionObserver triggers a LazyRegion mount, the chunk is
  // already fetched. requestIdleCallback defers to the browser's idle time
  // (after first-screen paint); a timeout is used as a fallback.
  useEffect(() => {
    const preload = () => {
      void R3Importer();
      void R4Importer();
      void R5Importer();
      void R6Importer();
      void R7Importer();
      void R8Importer();
      void R9Importer();
      void R10Importer();
      void R11Importer();
      void R12Importer();
    };
    const win = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    if (typeof win.requestIdleCallback === 'function') {
      const handle = win.requestIdleCallback(preload, { timeout: 2000 });
      return () => win.cancelIdleCallback?.(handle);
    }
    const handle = window.setTimeout(preload, 1500);
    return () => window.clearTimeout(handle);
  }, []);

  // While SM-1 is still in the Loading state, show a skeleton layout.
  if (playback.state.state === PlaybackState.Loading) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-md)',
          padding: 'var(--space-xl)',
        }}
      >
        <LoadingSkeleton height={32} width={240} />
        <LoadingSkeleton height={400} />
        <LoadingSkeleton height={72} />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        flex: 1,
      }}
    >
      <HeroSection />
      <PainPointSection />
      <R1ProductHeader />
      <R2ReplayMain
        runSession={runSession}
        frames={runSession.frames}
        pressureArrays={runSession.pressure_arrays}
        turns={runSession.turns}
      />

      {/* R3-R11: stacked content regions, lazy-loaded + IntersectionObserver-mounted */}
      <main style={{ display: 'flex', flexDirection: 'column', paddingBottom: 'var(--space-2xl)' }}>
        <LazyRegion><R3VirtualHardware /></LazyRegion>
        <LazyRegion><R4RunSummary /></LazyRegion>
        <DeepExploreSection
          title="技术深度探索"
          subtitle="算法实验室、3D 可视化、传感器波形、摔倒检测、手表蓝图、技术参数"
          defaultExpanded={true}
        >
          <LazyRegion><R5ScoringRadar /></LazyRegion>
          <LazyRegion><R6AlgorithmLab /></LazyRegion>
          <LazyRegion><R7Visualization3D /></LazyRegion>
          <LazyRegion><R8SensorWaveform /></LazyRegion>
          <LazyRegion><R9FallDetection /></LazyRegion>
          <LazyRegion><R10WatchShowcase /></LazyRegion>
          <LazyRegion><R11TechDepth /></LazyRegion>
        </DeepExploreSection>
      </main>

      {/* R12: footer, lazy-loaded + IntersectionObserver-mounted */}
      <LazyRegion><R12Footer /></LazyRegion>
    </div>
  );
}

/** Suspense fallback for lazy-loaded regions. */
function RegionFallback({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: 'var(--space-xl)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-md)',
      }}
    >
      <LoadingSkeleton height={28} width={200} />
      <LoadingSkeleton height={20} width="80%" />
      <span style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-sm)' }}>
        {label}
      </span>
    </div>
  );
}

/**
 * LazyRegion - IntersectionObserver-based lazy mounting wrapper.
 *
 * Wraps a lazy-loaded region (R3-R12). The region's component tree is not
 * mounted until its placeholder scrolls within `rootMargin` of the viewport,
 * keeping the first-screen paint focused on R1 + R2. Once visible, the region
 * mounts inside a Suspense boundary with a LoadingSkeleton fallback. The
 * observer disconnects after the first intersection so the region stays
 * mounted thereafter.
 */
function LazyRegion({
  children,
  rootMargin = '300px',
  minHeight = 120,
}: {
  children: ReactNode;
  rootMargin?: string;
  minHeight?: number;
}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [rootMargin]);

  return (
    <div ref={ref} style={{ minHeight: visible ? 0 : minHeight }}>
      {visible ? (
        <Suspense fallback={<RegionFallback label="加载区域…" />}>{children}</Suspense>
      ) : null}
    </div>
  );
}

// ===== Root App =====

export default function App() {
  const [pipelineResult, setPipelineResult] =
    useState<DiagnosticPipelineResult | null>(null);
  const didComputeRef = useRef(false);

  // On mount: synthesize the simulation data and run the diagnostic pipeline.
  // The ref guard prevents double computation under React 18 StrictMode.
  useEffect(() => {
    if (didComputeRef.current) return;
    didComputeRef.current = true;

    const script = getTurnScript();
    const { frames, pressureArrays } = generateSimulationData(script);
    const result = runDiagnosticPipeline(frames, pressureArrays, script);
    setPipelineResult(result);
  }, []);

  if (!pipelineResult) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-secondary)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--fs-md)',
        }}
      >
        加载中…
      </div>
    );
  }

  return (
    <PlaybackMachineProvider>
      <AppContent pipelineResult={pipelineResult} />
    </PlaybackMachineProvider>
  );
}
